import type { Map as MapLibreMap, MapMouseEvent } from 'maplibre-gl';

import type { MapPlotOptions } from '../../../PageConfigProvider/types/item';
import type { MetricValues } from '../../../PageConfigProvider/types/metric';
import {
  LocationType,
  type MapHoverHandler,
  type MapPoint,
  type MapRenderer,
} from '../types';
import {
  getColorScaleInfo,
  getMapThemeColors,
  type MapThemeColors,
  type MapThemeName,
} from '../utils/color-scale';
import { MAX_ZOOM, MIN_ZOOM } from '../utils/constants';
import { getMetricPaletteConfig } from '../utils/metric-scales';
import { type ChoroplethLayerConfig, LAYER_CONFIGS } from './ChoroplethConfig';
import {
  type LayerStatsByLocation,
  normalizeChoroplethData,
} from './dataManager';
import { LayerManager } from './layerManager';
import { buildFillExpression } from './paintExpressionBuilder';

export class ChoroplethRenderer implements MapRenderer {
  private map: MapLibreMap | null = null;

  private cleanupOverviewHandlers: (() => void) | undefined;

  private readonly layerManager: LayerManager;
  private isZooming = false;
  private isMapLoaded = false;
  private pendingUpdateArgs: Parameters<ChoroplethRenderer['update']> | null =
    null;
  private currentHighlight: {
    configId: string;
    key: string;
    featureId: string | number;
  } | null = null;
  private isInteractive = true;
  private readonly enabledConfigs: ChoroplethLayerConfig[];
  private readonly configBySourceId: Map<string, ChoroplethLayerConfig>;
  private readonly fillLayerIds: string[];
  private colors: MapThemeColors;
  private handlersAttached = false;
  private hoverCanvas: HTMLCanvasElement | null = null;
  private initOptions?: {
    // biome-ignore lint/suspicious/noConfusingVoidType: migrated from develop
    initializeTooltipHandlers?: (map: MapLibreMap) => void | (() => void);
    onHover?: MapHoverHandler;
    interactive?: boolean;
  };

  private tryInitializeLayers = () => {
    if (!this.map?.isStyleLoaded()) return;

    const missingLayers = this.enabledConfigs.some(
      (config) => !this.map?.getLayer(config.fillLayerId)
    );

    if (missingLayers) {
      this.enabledConfigs.forEach((config) => {
        this.layerManager.addSource(config);
        this.layerManager.addLayers(config, '');
      });
      if (this.isMapLoaded && this.pendingUpdateArgs) {
        this.performUpdate(...this.pendingUpdateArgs);
      }
    }

    if (!this.handlersAttached) {
      this.handlersAttached = true;
      if (this.initOptions?.initializeTooltipHandlers) {
        const cleanup = this.initOptions.initializeTooltipHandlers(this.map);
        this.cleanupOverviewHandlers =
          typeof cleanup === 'function' ? cleanup : undefined;
      }
      if (this.isInteractive || this.initOptions?.onHover) {
        this.map.on('mousemove', this.handleHover);
        this.hoverCanvas = this.map.getCanvas();
        this.hoverCanvas.addEventListener('mouseleave', this.handleLeave);
      }
    }
  };

  constructor(
    usesStateBreakdownOverMsa = false,
    mapTheme: MapThemeName = 'light'
  ) {
    this.colors = getMapThemeColors(mapTheme);
    this.layerManager = new LayerManager(this.colors);
    const allConfigs = LAYER_CONFIGS;

    this.enabledConfigs = usesStateBreakdownOverMsa
      ? allConfigs.filter(
          (c) => c.id !== LocationType.METRO_AREA && c.id !== LocationType.CITY
        )
      : allConfigs.filter(
          (c) => c.id !== LocationType.STATE && c.id !== LocationType.COUNTY
        );

    this.configBySourceId = new Map(
      this.enabledConfigs.map((cfg) => [cfg.sourceId, cfg])
    );
    this.fillLayerIds = this.enabledConfigs.map((cfg) => cfg.fillLayerId);
  }

  init(
    map: MapLibreMap,
    options?: {
      // biome-ignore lint/suspicious/noConfusingVoidType: migrated from develop
      initializeTooltipHandlers?: (map: MapLibreMap) => void | (() => void);
      onHover?: MapHoverHandler;
      interactive?: boolean;
    }
  ) {
    this.map = map;
    this.layerManager.init(map);
    this.isInteractive = options?.interactive ?? true;
    this.initOptions = options;

    map.on('style.load', this.tryInitializeLayers);
    map.on('idle', this.tryInitializeLayers);
    this.tryInitializeLayers();

    map.on('zoomstart', this.handleZoomStart);
    map.on('zoomend', this.handleZoomEnd);

    if (map.loaded()) {
      this.isMapLoaded = true;
    } else {
      // 'load' fires only once per Map instance, so a renderer attached after
      // map.setStyle() would never become ready — 'idle' fires after every
      // style swap settles. Replay the last update that bailed while loading.
      map.once('idle', () => {
        this.isMapLoaded = true;
        if (this.pendingUpdateArgs) {
          this.performUpdate(...this.pendingUpdateArgs);
        }
      });
    }
  }

  private handleZoomStart = () => {
    this.isZooming = true;
  };

  private handleZoomEnd = () => {
    this.isZooming = false;
  };

  private handleHover = (e: MapMouseEvent) => {
    if (!this.map) return;

    if (this.isZooming) {
      this.clearHover();
      return;
    }

    const features = this.map.queryRenderedFeatures(e.point, {
      layers: this.fillLayerIds,
    });

    const top = features[0];
    const config = top
      ? this.configBySourceId.get(String(top.source))
      : undefined;
    if (!top || !config) {
      this.clearHover();
      return;
    }

    const props = top.properties ?? {};
    const key = String(props[config.propertyName] ?? '');
    if (!key) {
      this.clearHover();
      return;
    }

    if (this.isInteractive) {
      const fallbackId = props[config.promoteId ?? config.propertyName];
      const featureId = top.id ?? fallbackId ?? key;
      if (
        this.currentHighlight?.configId !== config.id ||
        this.currentHighlight?.key !== key
      ) {
        this.setHighlight(config, key, featureId);
      }
    }

    this.map.getCanvas().style.cursor = 'pointer';
    this.initOptions?.onHover?.(props, config.fillLayerId, [
      e.lngLat.lng,
      e.lngLat.lat,
    ]);
  };

  private setHighlight(
    config: ChoroplethLayerConfig,
    key: string,
    featureId: string | number
  ) {
    this.clearHighlight();
    this.layerManager.setHoverState(config, featureId, true);
    this.currentHighlight = { configId: config.id, key, featureId };
  }

  private clearHighlight() {
    if (!this.currentHighlight) return;
    const config = this.enabledConfigs.find(
      (cfg) => cfg.id === this.currentHighlight?.configId
    );
    if (config) {
      this.layerManager.setHoverState(
        config,
        this.currentHighlight.featureId,
        false
      );
    }
    this.currentHighlight = null;
  }

  private clearHover() {
    if (!this.map) return;
    this.clearHighlight();
    this.map.getCanvas().style.cursor = '';
    this.initOptions?.onHover?.(null, null, null);
  }

  private handleLeave = () => {
    this.clearHover();
  };

  update(
    points: MapPoint[],
    metric?: MetricValues,
    options?: {
      mapOptions?: MapPlotOptions;
    }
  ) {
    if (!this.map) return;
    this.pendingUpdateArgs = [points, metric, options];
    this.performUpdate(points, metric, options);
  }

  setTheme(theme: MapThemeName) {
    this.colors = getMapThemeColors(theme);
    this.layerManager.setColors(this.colors);
    this.enabledConfigs.forEach((config) => {
      this.layerManager.applyThemeColors(config);
    });
    if (this.pendingUpdateArgs) {
      this.performUpdate(...this.pendingUpdateArgs);
    }
  }

  private performUpdate(
    points: MapPoint[],
    metric?: MetricValues,
    options?: {
      mapOptions?: MapPlotOptions;
    }
  ) {
    if (!this.map || !this.isMapLoaded) return;

    const colorScheme = getMetricPaletteConfig(metric);

    const userMapOptions = options?.mapOptions ?? {};
    const effectiveMapOptions: MapPlotOptions = {
      normalization: userMapOptions.normalization ?? colorScheme.normalization,
      transformation:
        userMapOptions.transformation ?? colorScheme.transformation,
    };

    const choroplethData = normalizeChoroplethData(
      points,
      this.enabledConfigs,
      {
        metric,
        mapOptions: effectiveMapOptions,
      }
    );

    const { byLayer: valueByLayerId } = choroplethData.values;

    const colorScales = this.computeColorScales(
      choroplethData.layers,
      colorScheme
    );

    this.enabledConfigs.forEach((config) => {
      const data = valueByLayerId.get(config.id) || {};
      const colorScale = colorScales[config.id];
      const fillColorExpr = buildFillExpression(
        config,
        data,
        colorScale,
        this.colors
      );
      this.layerManager.setDataFilter(config, Object.keys(data));
      this.layerManager.setPaint(config, fillColorExpr);
      this.layerManager.setOpacity(config, 1, 1);

      const minz = config.minzoom ?? MIN_ZOOM;
      const maxz = config.maxzoom ?? MAX_ZOOM;
      this.layerManager.setZoomRange(config, minz, maxz);
    });
  }

  destroy() {
    if (!this.map) return;

    this.map.off('style.load', this.tryInitializeLayers);
    this.map.off('idle', this.tryInitializeLayers);

    if (this.cleanupOverviewHandlers) {
      this.cleanupOverviewHandlers();
      this.cleanupOverviewHandlers = undefined;
    }

    this.map.off('mousemove', this.handleHover);
    this.hoverCanvas?.removeEventListener('mouseleave', this.handleLeave);
    this.hoverCanvas = null;

    this.map.off('zoomstart', this.handleZoomStart);
    this.map.off('zoomend', this.handleZoomEnd);

    this.layerManager.removeAll(this.enabledConfigs);
  }

  private computeColorScales(
    layers: LayerStatsByLocation,
    colorScheme: ReturnType<typeof getMetricPaletteConfig>
  ): Record<LocationType, (value: number) => string> {
    const defaultColorScale = getColorScaleInfo(
      [],
      colorScheme.type,
      colorScheme.direction,
      this.colors,
      colorScheme.domain
    ).color;
    const colorScales: Record<LocationType, (value: number) => string> = {
      [LocationType.REGION]: defaultColorScale,
      [LocationType.COUNTRY]: defaultColorScale,
      [LocationType.STATE]: defaultColorScale,
      [LocationType.COUNTY]: defaultColorScale,
      [LocationType.METRO_AREA]: defaultColorScale,
      [LocationType.CITY]: defaultColorScale,
    };

    this.enabledConfigs.forEach((config) => {
      const stats = layers[config.id];
      const { color } = getColorScaleInfo(
        stats.values,
        colorScheme.type,
        colorScheme.direction,
        this.colors,
        colorScheme.domain
      );
      colorScales[config.id] = color;
    });

    return colorScales;
  }
}
